import Edit from '@mui/icons-material/Edit';
import { Box, IconButton } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../provider/AuthProvider';
import { useHttpClient } from '../../provider/HttpClientProvider';
import { useOrgOptions } from '../../provider/OrganizationOptionsProvider';
import { useGetUsers } from '../../provider/RentalSessionsWrapperProvider';
import { SMALL_SCREEN_SIZE, useScreenDimensions } from '../../provider/ScreenDimensionsProvider';
import RentalSessionAdminEditPopup from '../../RentalSessions/RentalSessionPopUps/AdminEditPopUp/RentalSessionAdminEditPopUp';
import Alert from '../Alert/Alert';
import ExportDailyStatisticsExcel from '../ExportDailyStatisticsExcel/ExportDailyStatisticsExcel';
import { RENTAL_SESSION_QUERY_KEY } from '../utils/queryConstants';
import { convertDateRangeToTimestamp } from '../utils/timestampUtils';
import './RentalSessionHistoryTable.css';

const emptyObjWithDocs = {
    docs: [],
};

const emptyObj = {};

const emptyArray = [];

function RentalSessionHistoryTable() {
    const queryClient = useQueryClient();
    const httpClient = useHttpClient();
    const { isAdmin } = useAuth();
    const { screenWidth } = useScreenDimensions();
    const { currency, hideSum, loaded: orgOptionsLoaded, fitpass } = useOrgOptions();

    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 5,
    });
    const [dateRange, setDateRange] = useState({
        startDate: dayjs(Date.now()),
        endDate: dayjs(Date.now()),
    });
    const [editState, setEditState] = useState({ editing: false, curSession: null });
    const [error, setError] = useState();

    const {
        data = emptyObjWithDocs,
        isError: isRentalSessionsError,
        isFetching: isRentalSessionsFetching,
        isLoading: isRentalSessionsLoading,
        isPreviousData,
    } = useGetRentalSessions(pagination.pageIndex, pagination.pageSize, fetchData, dateRange);

    const {
        data: paymentsSum = emptyObj,
        isError: isRentalSessionsPaymentsSumError,
        isFetching: isRentalSessionsPaymentsSumFetching,
        isLoading: isRentalSessionsPaymentsSumLoading,
    } = useGetRentalSessionsPaymentsSum(dateRange);

    const { mutateAsync: edit } = useUpdateRentalSession(setError);

    useEffect(() => {
        if (!isPreviousData && data?.hasNextPage) {
            queryClient.prefetchQuery({
                queryKey: [
                    RENTAL_SESSION_QUERY_KEY,
                    pagination.pageIndex + 1,
                    pagination.pageSize,
                    dateRange,
                ],
                queryFn: () =>
                    fetchData(httpClient, pagination.pageIndex + 1, pagination.pageSize, dateRange),
            });
        }
    }, [
        data,
        isPreviousData,
        dateRange,
        pagination.pageSize,
        pagination.pageIndex,
        queryClient,
        httpClient,
    ]);

    const handleCloseEditing = useCallback(() => {
        setEditState({ editing: false, curSession: null });
    }, [setEditState]);

    const handleDatePick = useCallback(
        (value, targetedDate) => {
            setDateRange((curDateRange) => ({ ...curDateRange, [targetedDate]: value }));
        },
        [setDateRange]
    );

    const columns = useMemo(
        () => [
            {
                header: 'Rental Name',
                accessorKey: 'rentalName',
            },
            {
                header: 'Category',
                accessorKey: 'rentalCategoryName',
            },
            {
                header: 'Session Type',
                accessorKey: 'priceType',
            },
            {
                header: 'Total Price',
                accessorKey: 'totalPrice.$numberDecimal',
            },
            {
                header: 'Cash Payment',
                accessorFn: (dataRow) => {
                    return dataRow.paymentCash ? dataRow.paymentCash.$numberDecimal : 0;
                },
            },
            {
                header: 'Credit Payment',
                accessorFn: (dataRow) => {
                    return dataRow.paymentCredit ? dataRow.paymentCredit.$numberDecimal : 0;
                },
            },
            ...(!!fitpass
                ? [
                      {
                          header: 'Fitpass Payment',
                          accessorFn: (dataRow) => {
                              return dataRow.paymentFitpass
                                  ? dataRow.paymentFitpass.$numberDecimal
                                  : 0;
                          },
                      },
                  ]
                : []),
            {
                header: 'Date',
                accessorFn: (dataRow) => {
                    const startDate = new Date(parseInt(dataRow.startTimestamp));

                    if (dataRow.endTimestamp) {
                        const endDate = new Date(parseInt(dataRow.endTimestamp));
                        return (
                            startDate.toLocaleString('it-IT') +
                            ' - ' +
                            endDate.toLocaleTimeString('it-IT')
                        );
                    } else {
                        return startDate.toLocaleString('it-IT');
                    }
                },
            },
            {
                header: 'Status',
                accessorFn: (dataRow) => (dataRow.live ? 'LIVE' : 'ENDED'),
                maxSize: 100,
            },
        ],
        [fitpass]
    );

    const table = useMaterialReactTable({
        columns,

        data: data.docs,

        enableColumnOrdering: false,

        enableDensityToggle: false,

        enableRowActions: true,

        enableColumnFilters: false,

        enableSorting: false,

        enableColumnFilterModes: false,

        enableHiding: false,

        enableColumnActions: false,

        enablePagination: true,

        manualPagination: true,

        enableToolbarInternalActions: false,

        rowCount: data.totalDocs,

        positionPagination: screenWidth > SMALL_SCREEN_SIZE ? 'both' : 'bottom',

        enableBottomToolbar: true,

        positionToolbarDropZone: 'none',

        positionActionsColumn: 'last',

        layoutMode: 'grid',

        getRowId: (row) => row._id,

        renderDetailPanel: ({ row }) => <HistoryDetailPanel session={row.original} />,
        muiTableHeadCellProps: {},

        muiTableBodyRowProps: {
            hover: false,
        },

        muiTableContainerProps: {
            sx: {
                overflowY: 'scroll',
                flex: 1,
            },
        },

        muiSearchTextFieldProps: {
            sx: {
                width: '10rem',
            },
        },

        muiTopToolbarProps: {
            sx: {
                '& > div:nth-of-type(2)': {
                    width: '100%',
                    flex: '1',
                },
                '&': {
                    display: 'flex',
                    flexDirection: 'row',
                    margin: '0.2rem 0',
                },
            },
        },

        muiBottomToolbarProps: {
            sx: {
                '& > *': {
                    ...(screenWidth < SMALL_SCREEN_SIZE ? { flexDirection: 'column' } : {}),
                },
            },
        },

        muiPaginationProps: {
            size: 'medium',
            sx: {
                margin: '0',
            },
        },

        onPaginationChange: setPagination,

        renderTopToolbarCustomActions: () => (
            <div className="topTolbarCustomActions-wrapper">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer components={['DatePicker', 'DatePicker']}>
                        <DatePicker
                            name="startDate"
                            label="Start Date"
                            value={dateRange.startDate}
                            onChange={(newValue) => handleDatePick(newValue, 'startDate')}
                            className="datePicker"
                        />

                        <DatePicker
                            name="startDate"
                            label="End Date"
                            value={dateRange.endDate}
                            onChange={(newValue) => handleDatePick(newValue, 'endDate')}
                            className="datePicker"
                        />
                    </DemoContainer>
                </LocalizationProvider>
                <ExportDailyStatisticsExcel onError={setError} />
            </div>
        ),

        renderBottomToolbarCustomActions: () =>
            (orgOptionsLoaded && !hideSum?.hide) || isAdmin ? (
                <div className="history-paymentsSum-container">
                    <div>
                        <b>Cash: </b>
                        <i>
                            {parseFloat(paymentsSum.cash?.$numberDecimal ?? 0).toFixed(2) +
                                currency.sign}
                        </i>
                    </div>
                    <div>
                        <b>Credit: </b>
                        <i>
                            {parseFloat(paymentsSum.credit?.$numberDecimal ?? 0).toFixed(2) +
                                currency.sign}
                        </i>
                    </div>
                    {!!fitpass && (
                        <div>
                            <b>Fitpass: </b>
                            <i>
                                {parseFloat(paymentsSum.fitpass?.$numberDecimal ?? 0).toFixed(2) +
                                    currency.sign}
                            </i>
                        </div>
                    )}
                    <div>
                        <b>Grand Total: </b>
                        <i>
                            {(
                                parseFloat(paymentsSum.cash?.$numberDecimal ?? 0) +
                                parseFloat(paymentsSum.credit?.$numberDecimal ?? 0) +
                                parseFloat(paymentsSum.fitpass?.$numberDecimal ?? 0)
                            ).toFixed(2) + currency.sign}
                        </i>
                    </div>
                </div>
            ) : null,

        renderRowActions: ({ row }) => (
            <Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
                <IconButton
                    disabled={!isAdmin}
                    onClick={() => {
                        setEditState({ editing: true, curSession: row.original });
                    }}>
                    <Edit />
                </IconButton>
            </Box>
        ),

        state: {
            isLoading: isRentalSessionsLoading || isRentalSessionsPaymentsSumLoading,
            showAlertBanner: isRentalSessionsError || isRentalSessionsPaymentsSumError,
            showProgressBars: isRentalSessionsFetching || isRentalSessionsPaymentsSumFetching,
            pagination,
        },

        initialState: {
            expanded: false,
        },
    });

    return (
        <>
            <MaterialReactTable table={table} />
            {editState.editing ? (
                <RentalSessionAdminEditPopup
                    session={editState.curSession}
                    onClose={handleCloseEditing}
                    onEdit={edit}
                />
            ) : null}
            <Alert message={error} setMessage={setError} />
        </>
    );
}

export default RentalSessionHistoryTable;

function HistoryDetailPanel({ session }) {
    const { data: users = emptyArray } = useGetUsers();

    const usernamesMap = useMemo(
        () =>
            users.reduce((curMap, user) => {
                return { ...curMap, [user._id]: user.username };
            }, {}),
        [users]
    );

    return (
        <div className="history-detail-wrapper">
            <div className="history-detail">
                <h4>Pricing</h4>
                <span>
                    <h5>Hourly Rate:</h5>
                    <span className="history-detail-type-value">
                        {session.hourlyRate?.$numberDecimal}
                    </span>
                </span>
            </div>
            <div className="history-detail">
                <h4>Rental Types:</h4>

                {session.rentalTypes?.length ? (
                    session.rentalTypes.map((type) => (
                        <h5 className="history-detail-type-value" key={type._id}>
                            {type.name}
                        </h5>
                    ))
                ) : (
                    <i>No RentalTypes Used</i>
                )}
            </div>
            <div className="history-detail">
                <h4>Used Products:</h4>
                {session.usedProducts?.length ? (
                    session.usedProducts.map((product) => (
                        <div key={product._id} className="history-detail-item">
                            <h5 className="history-detail-type-value">{product.name}</h5>
                            <span className="history-detail-type-value">{product.quantity}</span>
                        </div>
                    ))
                ) : (
                    <i>No Products Used</i>
                )}
            </div>
            <div className="history-detail">
                <h4>Event:</h4>
                <div className="event-wrapper">
                    <table>
                        <tbody>
                            {session.events.map((product) => (
                                <tr key={product._id}>
                                    <td className="history-detail-table-item history-detail-table-username-item">
                                        {product.type}
                                    </td>
                                    <td className="history-detail-table-item">
                                        {usernamesMap[product.userId] ?? product.userId}
                                    </td>
                                    <td className="history-detail-table-item">
                                        {new Date(parseInt(product.timestamp)).toLocaleTimeString(
                                            'it-IT'
                                        )}
                                    </td>
                                    <td className="history-detail-table-item">
                                        {product.message ?? '---'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

const fetchData = async (httpClient, pageIndex, pageSize, dateRange) => {
    return await httpClient
        .get(
            `/rentalSession/history?page=` +
                (pageIndex + 1) +
                '&pageSize=' +
                pageSize +
                (dateRange.startDate
                    ? '&startDate=' + convertDateRangeToTimestamp(dateRange.startDate.$d)
                    : '') +
                (dateRange.endDate
                    ? '&endDate=' + convertDateRangeToTimestamp(dateRange.endDate.$d)
                    : '')
        )
        .then((result) => {
            if (result.status === 200) {
                return result.data;
            }
        });
};

function useGetRentalSessions(pageIndex, pageSize, fetchData, dateRange) {
    const httpClient = useHttpClient();
    return useQuery({
        queryKey: [RENTAL_SESSION_QUERY_KEY, pageIndex, pageSize, dateRange],
        queryFn: () => fetchData(httpClient, pageIndex, pageSize, dateRange),
        keepPreviousData: true,
        staleTime: 5000,
    });
}

function useGetRentalSessionsPaymentsSum(dateRange) {
    const httpClient = useHttpClient();
    return useQuery({
        queryKey: [RENTAL_SESSION_QUERY_KEY, dateRange],
        queryFn: async () => {
            return await httpClient
                .get(
                    `/rentalSession/history/paymentsSum?` +
                        (dateRange.startDate
                            ? '&startDate=' + convertDateRangeToTimestamp(dateRange.startDate.$d)
                            : '') +
                        (dateRange.endDate
                            ? '&endDate=' + convertDateRangeToTimestamp(dateRange.endDate.$d)
                            : '')
                )
                .then((result) => {
                    if (result.status === 200) {
                        return result.data;
                    }
                });
        },
    });
}

function useUpdateRentalSession(setError) {
    const queryClient = useQueryClient();
    const httpClient = useHttpClient();
    return useMutation({
        mutationFn: async (editedRentalSessionInfo) => {
            return await httpClient
                .put('/rentalSession/edit', editedRentalSessionInfo)
                .then(() => {
                    return true;
                })
                .catch((err) => {
                    setError(err.response.data.error.message);
                    return false;
                });
        },
        onSettled: () => queryClient.invalidateQueries({ queryKey: [RENTAL_SESSION_QUERY_KEY] }),
    });
}

